import type { TableDefinitionInputV1, TableTypeEnum } from '@/sdk/model/TableDefinitionInputV1';
import { BatchActionEnum } from '@/sdk/model/TableDefinitionInputV1';
import type { SearchTypeEnum } from '@/sdk/model/PropertySearchV1';
import type {
  ColumnDefinition,
  ItemTableCell,
  MaterializedTableOutput,
  ModelToSelect,
  ProcessedMaterializedTable,
  RowLoadProgress,
  TreeHierarchy,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { GridApi } from 'ag-grid-community';
import type { PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import {
  DEFAULT_COLUMN_DEFINITION_INPUTS,
  DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  columnDefinitionOutputToColumnDefinitionInput,
  generateTableName,
  getColumnDisplayName,
  getColumnsNotIncludedInAgGrid,
  tableDefinitionOutputToTableDefinitionInput,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import type { ColumnDefinitionInputV1 } from '@/sdk/model/ColumnDefinitionInputV1';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { transpose } from '@/utilities/utilities';
import { createSelector } from 'reselect';
import { columnRuleOutputToColumnRuleInput } from '@/tableDefinitionEditor/columnRules/columnRule.utilities';
import type { ColumnRule } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import _ from 'lodash';
import type { ModelTableApplicationsOutputListV1 } from '@/sdk/model/ModelTableApplicationsOutputListV1';
import type { ItemSearchPreviewV1 } from '@/sdk/model/ItemSearchPreviewV1';
import type { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import type { RowRulesResultV1 } from '@/sdk/model/RowRulesResultV1';
import type { RulesResultV1 } from '@/sdk/model/RulesResultV1';
import type { ColumnDefinitionBulkEditInputV1 } from '@/sdk/model/ColumnDefinitionBulkEditInputV1';
import type { HierarchyOutputV1 } from '@/sdk/model/HierarchyOutputV1';
import type { ItemFinderOutputV1 } from '@/sdk/model/ItemFinderOutputV1';
import { isHierarchySearch } from '@/itemFinder/itemFinder.utilities';

/**
 * Base class store for the table definition
 * The table definition state is stored here
 * which is updated when we receive messages from the server on the socket for table definitions.
 * This base class contains all handler functions, getters and state management that can be used in other editor stores
 */
export abstract class BaseTableDefinitionStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  abstract defaultName: string;
  setupState(defaultType: TableTypeEnum) {
    this.state = this.immutable({
      id: '',
      subscriberId: '',
      name: '',
      searchTypeAI: undefined,
      description: '',
      scopedTo: undefined,
      batchAction: BatchActionEnum.UPDATEEXISTINGINSERTNEWCLEANUP,
      tableType: defaultType,
      columns: [],
      hasUnsupportedSubscription: undefined,
      shouldSearchForItems: false,
      tableDefinitionInput: this.monkey(
        ['subscriberId'],
        ['name'],
        ['description'],
        ['scopedTo'],
        ['batchAction'],
        ['tableType'],
        ['columns'],
        function (
          subscriberId: string,
          name: string,
          description: string,
          scopedTo: string | undefined,
          batchAction: BatchActionEnum,
          tableType: TableTypeEnum,
          columnDefinitions: ColumnDefinition[],
        ): TableDefinitionInputV1 {
          const columnDefinitionInputs =
            columnDefinitions.length === 0
              ? DEFAULT_COLUMN_DEFINITION_INPUTS
              : columnDefinitions.map((column) =>
                  columnDefinitionOutputToColumnDefinitionInput(column, columnDefinitions),
                );

          return {
            subscriptionId: subscriberId,
            name,
            description,
            scopedTo,
            batchAction,
            tableType,
            columnDefinitions: columnDefinitionInputs,
          };
        },
      ),
      tableDefinitionOutput: undefined,
      materializedTable: undefined,
      itemFinder: undefined,
      hasHierarchySearch: this.monkey(['itemFinder'], (itemFinder: ItemFinderOutputV1 | undefined) => {
        if (itemFinder) {
          return itemFinder.searches.some((search) => isHierarchySearch(search));
        }
        return false;
      }),
      doTableReload: false,
      itemColumnsThatCouldBeUsedToAutoCreateTextColumns: this.monkey(
        ['columns'],
        ['tableType'],
        (columns: ColumnDefinition[], tableType: TableTypeEnum) => {
          return (
            columns
              .map((column, index) => ({ ...column, columnIndex: index + 1 }))
              .filter((column) => column.columnType === ColumnTypeEnum.UUID && column.displayProperty)
              .filter((column) => !getColumnsNotIncludedInAgGrid(tableType, columns).includes(column.columnName))
              // filter out an item column if there is a text column whose first rule is item property and has the same property
              // as the item column is currently displaying
              .filter((itemColumnWithProperty) => {
                const textColumnWithItemPropertyRule = columns.find((col) => {
                  const getItemPropertyRule =
                    col.columnType === ColumnTypeEnum.TEXT &&
                    col.rules.length > 0 &&
                    col.rules[0].rule === SeeqNames.MaterializedTables.Rules.GetItemProperty
                      ? columnRuleOutputToColumnRuleInput(
                          col.rules[0],
                          columns.filter((columnDef) => columnDef.columnName !== col.columnName),
                        )
                      : undefined;
                  const isPropertyMatch =
                    getItemPropertyRule?.getItemProperty?.propertyName === itemColumnWithProperty.displayProperty;
                  const inputColumnOfRuleIsItemColumn =
                    getItemPropertyRule?.getItemProperty?.columnIndex === itemColumnWithProperty.columnIndex;
                  return isPropertyMatch && inputColumnOfRuleIsItemColumn ? col : undefined;
                });
                return !textColumnWithItemPropertyRule;
              })
              .map((column) => ({
                columnName: column.columnName,
                displayProperty: column.displayProperty,
                displayName: column.displayName,
              })) as Required<Pick<ColumnDefinition, 'columnName' | 'displayName' | 'displayProperty'>>[]
          );
        },
      ),
      pendingTableIds: {},
      showEvaluationDetailsModal: false,
      evaluationDetailsColumn: {},
      createdItemFinderId: undefined,
      allowHiddenColumnsAsInputs: false,
      agGridApi: undefined,
      rowLoadProgress: undefined,
      rowDataIsLoading: false,
      selectedColumns: [],
      columnOptions: undefined,
      scrollToColumnId: '',
      rowItemIdsInsertedInGroupLevel: [],
      showCreateModel: false,
      isApplyingModel: false,
      modelInputToSelect: undefined,
      columnIdToBeUsedAsModelInput: undefined,
      modelColumns: undefined,
      isEditingModel: false,
      editModelPreviewColumns: undefined,
      hierarchiesUsingThisColumnAsGroup: [],
      columnToDelete: undefined,
    });
  }

  getName: (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => string =
    createSelector(
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.tableDefinitions,
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.id,
      (state: { tableDefinitions: ItemSearchPreviewV1[]; currentName: string; id: string }) => state.currentName,
      (tableDefinitions: ItemSearchPreviewV1[], id: string, currentName?: string) => {
        return generateTableName(id, tableDefinitions, this.defaultName, currentName);
      },
    );

  abstract getRulesNotAllowed(): ColumnRule[];

  abstract getMaterializedTableCancellationGroup(): string;
  abstract getTableDefinitionCancellationGroup(): string;

  get rowItemIdsInsertedInGroupLevel(): string[] {
    return this.state.get('rowItemIdsInsertedInGroupLevel');
  }

  get hierarchiesUsingThisColumnAsGroup(): HierarchyOutputV1[] {
    return this.state.get('hierarchiesUsingThisColumnAsGroup');
  }

  get columnToDelete(): ColumnDefinition | undefined {
    return this.state.get('columnToDelete');
  }

  get id(): string {
    return this.state.get('id');
  }

  get shouldSearchForItems(): boolean {
    return this.state.get('shouldSearchForItems');
  }

  get pendingTableIds(): Record<string, boolean> {
    return this.state.get('pendingTableIds');
  }

  get subscriberId(): string {
    return this.state.get('subscriberId');
  }

  get createdItemFinderId(): string {
    return this.state.get('createdItemFinderId');
  }

  abstract get name(): string;

  get tableType(): TableTypeEnum {
    return this.state.get('tableType');
  }

  get searchTypeAI(): SearchTypeEnum | undefined {
    return this.state.get('searchTypeAI');
  }

  get description(): string {
    return this.state.get('description');
  }

  get scopedTo(): string | undefined {
    return this.state.get('scopedTo');
  }

  get batchAction(): BatchActionEnum {
    return this.state.get('batchAction');
  }

  get columns(): ColumnDefinition[] {
    return this.state.get('columns');
  }

  get tableDefinitionInput(): TableDefinitionInputV1 {
    return this.state.get('tableDefinitionInput');
  }

  get tableDefinitionOutput(): TableDefinitionOutputV1 {
    return this.state.get('tableDefinitionOutput');
  }

  get materializedTable(): ProcessedMaterializedTable | undefined {
    return this.state.get('materializedTable');
  }

  get itemFinder(): ItemFinderOutputV1 | undefined {
    return this.state.get('itemFinder');
  }

  get showEvaluationDetailsModal(): boolean {
    return this.state.get('showEvaluationDetailsModal');
  }

  get evaluationDetailsColumn(): { evaluationColumnDef: ColumnDefinition; rulesResult: RulesResultV1[] } {
    return this.state.get('evaluationDetailsColumn');
  }

  get itemColumnsThatCouldBeUsedToAutoCreateTextColumns(): Required<
    Pick<ColumnDefinition, 'columnName' | 'displayName' | 'displayProperty'>
  >[] {
    return this.state.get('itemColumnsThatCouldBeUsedToAutoCreateTextColumns');
  }

  get hasHierarchySearch(): boolean {
    return this.state.get('hasHierarchySearch');
  }

  get hasUnsupportedSubscription(): boolean | undefined {
    return this.state.get('hasUnsupportedSubscription');
  }

  get doTableReload(): boolean {
    return this.state.get('doTableReload');
  }

  get allowHiddenColumnsAsInputs(): boolean {
    return this.state.get('allowHiddenColumnsAsInputs');
  }

  get agGridApi(): GridApi | undefined {
    return this.state.get('agGridApi');
  }

  get selectedColumns(): string[] {
    return this.state.get('selectedColumns');
  }

  get showConfigureColumn(): boolean {
    return this.state.get('showConfigureColumn');
  }

  get columnOptions(): TreeHierarchy | undefined {
    return this.state.get('columnOptions');
  }

  get scrollToColumnId(): string {
    return this.state.get('scrollToColumnId');
  }

  getColumnFromProcessedMaterializedTable = (columnName: string): ItemTableCell[] => {
    const materializedTable = this.materializedTable;
    if (!materializedTable) {
      return [];
    }
    const rows = materializedTable.rows;
    const rowsAsColumns = transpose(rows);
    const columnIndex = materializedTable.headers.findIndex((header) => header.name === columnName);
    return rowsAsColumns[columnIndex] as unknown as ItemTableCell[];
  };

  get rowLoadProgress(): RowLoadProgress | undefined {
    return this.state.get('rowLoadProgress');
  }

  get rowDataIsLoading(): boolean {
    return this.state.get('rowDataIsLoading');
  }

  get showCreateModel(): boolean {
    return this.state.get('showCreateModel');
  }

  get isApplyingModel(): boolean {
    return this.state.get('isApplyingModel');
  }

  get modelInputToSelect(): ModelToSelect | undefined {
    return this.state.get('modelInputToSelect');
  }

  get columnIdToBeUsedAsModelInput(): string | undefined {
    return this.state.get('columnIdToBeUsedAsModelInput');
  }

  get modelColumns(): ModelTableApplicationsOutputListV1 {
    return this.state.get('modelColumns');
  }

  get isEditingModel(): boolean {
    return this.state.get('isEditingModel');
  }

  get editModelPreviewColumns(): ColumnDefinitionInputV1[] | ColumnDefinitionBulkEditInputV1[] | undefined {
    return this.state.get('editModelPreviewColumns');
  }

  protected readonly handlers = {};

  setSubscriberId = ({ subscriberId }: { subscriberId: string | undefined }) => {
    this.state.set('subscriberId', subscriberId);
  };

  setShouldSearchForItems = ({ shouldSearchForItems }: { shouldSearchForItems: boolean }) => {
    this.state.set('shouldSearchForItems', shouldSearchForItems);
  };

  setName = ({ name }: { name: string }) => {
    this.state.set('name', name);
  };

  setSearchTypeAI = ({ searchTypeAI }: { searchTypeAI: SearchTypeEnum }) => {
    this.state.set('searchTypeAI', searchTypeAI);
  };

  setDescription = ({ description }: { description: string }) => {
    this.state.set('description', description);
  };

  setBatchAction = ({ batchAction }: { batchAction: BatchActionEnum }) => {
    this.state.set('batchAction', batchAction);
  };

  setDoTableReload = ({ doTableReload }: { doTableReload: boolean }) => {
    this.state.set('doTableReload', doTableReload);
  };

  setItemFinder = ({ itemFinder }: { itemFinder: ItemFinderOutputV1 | undefined }) => {
    this.state.set('itemFinder', itemFinder);
  };

  setTableDefinition = ({ tableDefinition }: { tableDefinition: TableDefinitionOutputV1 }) => {
    this.state.set('tableDefinitionOutput', tableDefinition);

    this.state.set('id', tableDefinition.id);
    this.state.set('name', tableDefinition.name);

    const hasUnsupportedSubscription = tableDefinition.subscription?.type === SeeqNames.Types.ConditionMonitor;
    this.state.set('hasUnsupportedSubscription', hasUnsupportedSubscription);
    if (hasUnsupportedSubscription) {
      return;
    }

    const tableDefinitionInput = tableDefinitionOutputToTableDefinitionInput(tableDefinition, this.scopedTo);
    this.state.set('subscriberId', tableDefinitionInput.subscriptionId);
    this.state.set('description', tableDefinitionInput.description);
    this.state.set('scopedTo', tableDefinitionInput.scopedTo);
    this.state.set('batchAction', tableDefinitionInput.batchAction);
    this.state.set('tableType', tableDefinitionInput.tableType);
    const currentColumns: ColumnDefinition[] = this.state.get('columns');
    const newColumnDefinitions = tableDefinition.columnDefinitions;
    const updatedColumns: ColumnDefinition[] = newColumnDefinitions.map((newColumn) => {
      const existingColumn = currentColumns.find((column) => column.columnName === newColumn.columnName);
      const displayName = getColumnDisplayName(newColumn.columnName, this.tableDefinitionOutput.uniqueIDColumnName);
      const displayProperty = newColumn.columnType === ColumnTypeEnum.UUID ? newColumn?.displayProperty : null;
      const isUUIDColumn = newColumn.columnType === ColumnTypeEnum.UUID;
      const hideColumnAsInput = newColumn.rules.some(
        (rule) => rule.rule === SeeqNames.MaterializedTables.Rules.SetItemProperty,
      );
      const additionalProperties = isUUIDColumn
        ? existingColumn?.additionalProperties ?? DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS
        : undefined;
      const isHiddenFromDropdown = hideColumnAsInput || existingColumn?.isHiddenFromDropdown;
      return {
        displayName,
        ...(displayProperty !== null && { displayProperty }),
        additionalProperties,
        isHiddenFromDropdown,
        ...newColumn,
      };
    });
    this.state.set('columns', updatedColumns);
    if (!_.isEqual(currentColumns, updatedColumns)) {
      this.state.set('doTableReload', true);
    }
  };

  setMaterializedTable = ({
    materializedTable,
    columnDefinitions,
  }: {
    materializedTable: MaterializedTableOutput;
    columnDefinitions: ColumnDefinition[] | undefined;
  }) => {
    if (columnDefinitions) {
      this.state.set('columns', columnDefinitions);
    }
    const columns: ColumnDefinition[] = columnDefinitions ?? this.state.get('columns');
    const processedMaterializedTable = processMaterializedTable(materializedTable, columns);
    this.state.set('materializedTable', processedMaterializedTable);
  };

  setEvaluationResults = ({ evaluationResults }: { evaluationResults: { [key: string]: RowRulesResultV1[] } }) => {
    const columns: ColumnDefinition[] = this.state.get('columns');

    const updatedColumns: ColumnDefinition[] = columns.map((newColumn) => {
      return {
        evaluationDetails: evaluationResults[newColumn.id.toLowerCase()],
        ...newColumn,
      };
    });
    this.state.set('columns', updatedColumns);
  };

  reset = () => {
    this.initialize();
  };

  addPendingTableId = ({ pendingTableId }: { pendingTableId: string }) => {
    this.state.set(['pendingTableIds', pendingTableId], true);
  };

  removePendingTableId = ({ pendingTableId }: { pendingTableId: string }) => {
    this.state.unset(['pendingTableIds', pendingTableId]);
  };

  setShowEvaluationDetailsModal = ({ show }: { show: boolean }) => {
    this.state.set('showEvaluationDetailsModal', show);
  };

  setEvaluationDetailsColumn = ({
    evaluationColumnDef,
    rulesResult,
  }: {
    evaluationColumnDef: ColumnDefinition;
    rulesResult: RulesResultV1[];
  }) => {
    this.state.set('evaluationDetailsColumn', { evaluationColumnDef, rulesResult });
  };

  setCreatedItemFinderId = ({ createdItemFinderId }: { createdItemFinderId: string | undefined }) => {
    this.state.set('createdItemFinderId', createdItemFinderId);
  };

  setAllowHiddenColumnsAsInputs = ({ allowHiddenColumnsAsInputs }: { allowHiddenColumnsAsInputs: boolean }) => {
    this.state.set('allowHiddenColumnsAsInputs', allowHiddenColumnsAsInputs);
  };

  setAgGridApi = ({ agGridApi }: { agGridApi: GridApi }) => {
    this.state.set('agGridApi', agGridApi);
  };

  setRowDataIsLoading = ({ rowDataIsLoading }: { rowDataIsLoading: boolean }) => {
    this.state.set('rowDataIsLoading', rowDataIsLoading);
  };

  requestSetProgress = (payload: {
    id: string;
    progress: Record<string, unknown>;
    timingInformation?: Record<string, unknown>;
    meterInformation?: Record<string, unknown>;
  }) => {
    if (payload.id === this.id) {
      this.state.set('rowLoadProgress', {
        progress: payload.progress ?? 0,
        timingInformation: payload.timingInformation,
        meterInformation: payload.meterInformation,
      });
    }
  };

  setSelectedColumns = ({ selectedColumns }: { selectedColumns: string[] }) => {
    this.state.set('selectedColumns', selectedColumns);
  };

  setShowConfigureColumn = ({ show }: { show: boolean }) => {
    this.state.set('showConfigureColumn', show);
  };

  setColumnOptions = ({ columnOptions }: { columnOptions: TreeHierarchy | undefined }) => {
    this.state.set('columnOptions', columnOptions);
  };

  setScrollToColumnId = ({ scrollToColumnId }: { scrollToColumnId: string }) => {
    this.state.set('scrollToColumnId', scrollToColumnId);
  };

  setIsApplyingModel = ({ isApplyingModel }: { isApplyingModel: boolean }) => {
    this.state.set('isApplyingModel', isApplyingModel);
  };

  setModelInputToSelect = ({ modelInputToSelect }: { modelInputToSelect: ModelToSelect | undefined }) => {
    this.state.set('modelInputToSelect', modelInputToSelect);
  };

  setColumnIdToBeUsedAsModelInput = ({
    columnIdToBeUsedAsModelInput,
  }: {
    columnIdToBeUsedAsModelInput: string | undefined;
  }) => {
    this.state.set('columnIdToBeUsedAsModelInput', columnIdToBeUsedAsModelInput);
  };

  setModelColumns = ({ modelColumns }: { modelColumns: ModelTableApplicationsOutputListV1 | undefined }) => {
    this.state.set('modelColumns', modelColumns);
  };

  addRowItemIdsInsertedInGroupLevel = ({ ids }: { ids: string[] }) => {
    const allIds = ids.concat(this.rowItemIdsInsertedInGroupLevel);
    const uniqueIds = Array.from(new Set(allIds)).sort();
    if (!_.isEqual(uniqueIds, this.rowItemIdsInsertedInGroupLevel)) {
      this.state.set('rowItemIdsInsertedInGroupLevel', uniqueIds);
    }
  };

  clearRowItemIdsInsertedInGroupLevel = () => {
    this.state.set('rowItemIdsInsertedInGroupLevel', []);
  };

  setShowCreateModel = ({ show }: { show: boolean }) => {
    this.state.set('showCreateModel', show);
  };

  setIsEditingModel = ({ isEditingModel }: { isEditingModel: boolean }) => {
    this.state.set('isEditingModel', isEditingModel);
  };

  setEditModelPreviewColumns = ({
    editModelPreviewColumns,
  }: {
    editModelPreviewColumns: ColumnDefinitionInputV1[] | ColumnDefinitionBulkEditInputV1[] | undefined;
  }) => {
    this.state.set('editModelPreviewColumns', editModelPreviewColumns);
  };

  setColumns = ({ columns }: { columns: ColumnDefinition[] }) => {
    this.state.set('columns', columns);
  };

  setHierarchiesUsingThisColumnAsGroup = ({ hierarchies }: { hierarchies: HierarchyOutputV1[] }) => {
    this.state.set('hierarchiesUsingThisColumnAsGroup', hierarchies);
  };

  setColumnToDelete = ({ column }: { column: ColumnDefinition | undefined }) => {
    this.state.set('columnToDelete', column);
  };

  waitForAgGridApi = async (timeoutMs = 5000): Promise<GridApi> => {
    const agGridApi = this.agGridApi;
    if (agGridApi) {
      return agGridApi;
    }
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const checkApi = () => {
        const api = this.agGridApi;
        if (api) {
          resolve(api);
        } else if (Date.now() - start >= timeoutMs) {
          reject(new Error('Timed out waiting for agGridApi'));
        } else {
          setTimeout(checkApi, 50);
        }
      };
      checkApi();
    });
  };
}
